import { useQuery } from '@tanstack/react-query';
import { landingApi } from '../api/services.api';
import { useDebounce } from '../../../shared/hooks/useDebounce';

// ── Query-key factory ───────────────────────────────────────────
export const catalogKeys = {
  all: ['catalog'],
  categories: () => ['catalog', 'categories'],
  services: (subCategoryID) => ['catalog', 'services', subCategoryID],
  subServices: (serviceID, packageID) => ['catalog', 'subServices', serviceID, packageID],
  subServiceDetail: (subServiceID) => ['catalog', 'subServiceDetail', subServiceID],
  popular: () => ['catalog', 'popular'],
  discounted: () => ['catalog', 'discounted'],
  advertisement: () => ['catalog', 'advertisement'],
  coupons: () => ['catalog', 'coupons'],
  serviceVideos: () => ['catalog', 'serviceVideos'],
  theme: () => ['catalog', 'theme'],
  search: (searchKey, subCategoryID) => ['catalog', 'search', searchKey, subCategoryID],
};

const FIVE_MIN = 5 * 60 * 1000;

// Defensively coerce a payload into an array, checking common wrapper keys.
export const toArray = (d) => {
  if (Array.isArray(d)) return d;
  if (d && typeof d === 'object') {
    if (Array.isArray(d.services)) return d.services;
    if (Array.isArray(d.subServices)) return d.subServices;
    if (Array.isArray(d.data)) return d.data;
    if (Array.isArray(d.result)) return d.result;
    if (Array.isArray(d.categories)) return d.categories;
  }
  return [];
};

export const useCategories = () =>
  useQuery({
    queryKey: catalogKeys.categories(),
    queryFn: () => landingApi.getCategories(),
    staleTime: FIVE_MIN,
  });

export const useServices = (subCategoryID) =>
  useQuery({
    queryKey: catalogKeys.services(subCategoryID),
    queryFn: () => landingApi.getServices(subCategoryID),
    enabled: !!subCategoryID,
    staleTime: FIVE_MIN,
  });

export const useSubServices = (serviceID, packageID) =>
  useQuery({
    queryKey: catalogKeys.subServices(serviceID, packageID),
    queryFn: () => landingApi.getSubServices(serviceID, packageID),
    enabled: !!serviceID && !!packageID,
    staleTime: FIVE_MIN,
  });

export const useSubServiceDetail = (subServiceID) =>
  useQuery({
    queryKey: catalogKeys.subServiceDetail(subServiceID),
    queryFn: () => landingApi.getSubServiceDetail(subServiceID),
    enabled: !!subServiceID,
    staleTime: FIVE_MIN,
  });

export const usePopularServices = () =>
  useQuery({
    queryKey: catalogKeys.popular(),
    queryFn: () => landingApi.popularServices(),
    staleTime: FIVE_MIN,
  });

export const useDiscountedServices = () =>
  useQuery({
    queryKey: catalogKeys.discounted(),
    queryFn: () => landingApi.discountedServices(),
    staleTime: FIVE_MIN,
  });

export const useAdvertisement = () =>
  useQuery({
    queryKey: catalogKeys.advertisement(),
    queryFn: () => landingApi.advertisement(),
    staleTime: FIVE_MIN,
  });

export const useCoupons = () =>
  useQuery({
    queryKey: catalogKeys.coupons(),
    queryFn: () => landingApi.getCoupons(),
    staleTime: FIVE_MIN,
  });

export const useServiceVideos = () =>
  useQuery({
    queryKey: catalogKeys.serviceVideos(),
    queryFn: () => landingApi.serviceVideos(),
    staleTime: FIVE_MIN,
  });

export const useTheme = () =>
  useQuery({
    queryKey: catalogKeys.theme(),
    queryFn: () => landingApi.theme(),
    staleTime: FIVE_MIN,
  });

export const useServiceSearch = (searchKey, subCategoryID) => {
  const debounced = useDebounce(searchKey, 400);
  const key = (debounced ?? '').trim();
  return useQuery({
    queryKey: catalogKeys.search(key, subCategoryID),
    queryFn: () => landingApi.searchSubServices({ searchKey: key, subCategoryID }),
    enabled: key.length >= 2,
    staleTime: FIVE_MIN,
  });
};
